import { authApi } from '../api/auth';
import { getBootstrap } from '../config/bootstrap';
import { browser } from '../utils/browser';
import { setAccessToken, setCsrfToken } from './authStore';
import { callbackReturnUrl } from './callback';

/** sessionStorage key for the sign-in loop guard. The value is an epoch-ms TIMESTAMP ONLY. */
export const SIGN_IN_GUARD_KEY = 'cnpwks.signInRedirectAt';
export const SIGN_IN_GUARD_WINDOW_MS = 60_000;

export type SignInRedirectOutcome = 'redirected' | 'stalled';

/** `loginUrl` verbatim + `returnUrl` (encoded). `&` when loginUrl already has a query. */
export function signInUrl(loginUrl: string, returnUrl: string): string {
  const sep = loginUrl.includes('?') ? '&' : '?';
  return `${loginUrl}${sep}returnUrl=${encodeURIComponent(returnUrl)}`;
}

/** One silent refresh at page load. True only when an access token was stored. */
export async function bootRefresh(): Promise<boolean> {
  try {
    const body = await authApi.refresh();
    if (typeof body.accessToken !== 'string' || body.accessToken.length === 0) return false;
    setAccessToken(body.accessToken);
    setCsrfToken(body.csrfToken ?? null);
    return true;
  } catch {
    return false; // 401, 403, 5xx and network all mean "no session" at boot
  }
}

/**
 * Leave for centralized login, unless that would be a loop: a sign-in redirect already happened
 * within SIGN_IN_GUARD_WINDOW_MS and the app is back without a session → 'stalled', no navigation.
 * `force` (the "Sign in again" button) skips the check.
 * returnUrl is ${origin}/auth/callback?next=<current path> (the SASATH-0156 callback-path gate).
 */
export function redirectToSignIn(options: { force?: boolean } = {}): SignInRedirectOutcome {
  const now = Date.now();
  const last = readGuard();
  if (!options.force && last !== null && now - last >= 0 && now - last < SIGN_IN_GUARD_WINDOW_MS) {
    return 'stalled';
  }
  writeGuard(now);
  browser.assign(signInUrl(getBootstrap().loginUrl, callbackReturnUrl(window.location)));
  return 'redirected';
}

/** Called when the greeting renders: the round trip worked. */
export function clearSignInGuard(): void {
  try {
    window.sessionStorage.removeItem(SIGN_IN_GUARD_KEY);
  } catch {
    /* storage unavailable */
  }
}

function readGuard(): number | null {
  try {
    const raw = window.sessionStorage.getItem(SIGN_IN_GUARD_KEY);
    const n = raw === null ? NaN : Number(raw);
    return Number.isFinite(n) ? n : null;
  } catch {
    return null; // storage unavailable: no guard, redirect proceeds
  }
}

function writeGuard(at: number): void {
  try {
    window.sessionStorage.setItem(SIGN_IN_GUARD_KEY, String(at));
  } catch {
    /* storage unavailable */
  }
}
